import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import MapView from '@arcgis/core/views/MapView';
import { AppConstants } from 'cd-eft/appConstants';
import sleep from 'cd-eft/utils/sleep';

import { Extent, esriProjection } from 'cd-eft/utils/esriTypes';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import { union } from '@arcgis/core/geometry/geometryEngine';
import {
  InputPoint,
  LandLevelingResponse,
  LineSegmentDto,
  PadResponse,
} from 'cd-eft/_codegen_/openapi-generator/eft-set';
import {
  __centerPointSymbol,
  __slopeArrowLineSymbol,
  __landLevelingSlopeArrowLineSymbol,
  __wwSlopeArrowLineSymbol,
  __wwFlowArrowLineSymbol,
  __structureLineSymbol,
  __wwBreaklinesSymbol,
  __channelBottomSymbol,
  __wwCenterLineSymbol,
  __hiddenLineSymbol,
  __wwBorderPolygonSymbol,
  __trcSubbasinPolygonSymbol,
  __wwCrossPolygonSymbol,
  __wwLevelPolygonSymbol,
  __wwSeedingPolygonSymbol,
  __wwSeedingOutlineSymbol,
  __landLevelingFillSymbol,
  __landLevelingStakeoutGridPointsSymbol,
  __standardLabelSymbol,
  __lineLabelSymbol,
  __trcCrossLineSymbol,
  __trcBorderPolygonSymbol,
  __trcCutSlopePolygonSymbol,
  __trcSlopePolygonSymbol,
  __trcBottomPolygonSymbol,
  __trcRidgeTopPolygonSymbol,
  __blockStationMarkerSymbol,
  __drainStationMarkerSymbol,
  __landLevelingPlanePointsUnmodified,
  __landLevelingPlanePointsFill,
  __landLevelingPlanePointsCut,
  __ugoLineSymbol,
} from './esriSymbols';

import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Polygon from '@arcgis/core/geometry/Polygon';
import Geometry from '@arcgis/core/geometry/Geometry';
import Layer from '@arcgis/core/layers/Layer';
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import { getDimensionsForImage } from './imageUtils';

export type LinearUnit = 'meters' | 'intl-feet' | 'us-feet';
interface BreaklineObject {
  breaklineID: number;
  groupID: string;
  breaklinePoints: any[];
}
const __parallelLineLabels = new LabelClass({
  symbol: __lineLabelSymbol,
  labelPlacement: 'above-along',
  labelPosition: 'curved',
  repeatLabel: false,
  labelExpressionInfo: {
    expression: '$feature.featureType'
  },
});
const __polygonBorderLabels = new LabelClass({
  symbol: __lineLabelSymbol,
  labelPlacement: 'center-along',
  labelPosition: 'curved',
  repeatLabel: true,
  repeatLabelDistance: 1000,
  labelExpressionInfo: {
    expression: '$feature.featureType'
  },
});
const __terraceLineLabels = new LabelClass({
  symbol: __lineLabelSymbol,
  labelPlacement: 'center-along',
  labelPosition: 'curved',
  repeatLabel: true,
  repeatLabelDistance: 300,
  labelExpressionInfo: {
    expression: '$feature.featureType'
  },
});


export function constructDikedPitLayer(
  dikedPit: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  elementId,
  label?: any
): GroupLayer {
  const slopeArrowsLayer = constructSlopeArrowsLayerFromLineSegments(
    'Slope Arrows',
    dikedPit.slopeArrows,
    __slopeArrowLineSymbol,
    inputSR,
    outputSR
  );

  const dikedPitPadBorderLayer = constructBorderLinesLayer(
    //Exterior Border
    dikedPit.padBorders,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Exterior Border',
    elementId
  );

  const dikedPitPitInnerBorderLayer = constructBorderLinesLayer(
    //Bottom Border
    dikedPit.pitVertices,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Bottom Border',
    elementId
  );

  const dikedPitPitOuterBorderLayer = constructBorderLinesLayer(
    //Inner Top Edge
    dikedPit.pitBorders,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Inner Top Edge',
    elementId
  );

  const centerPointLayer = constructCenterPointLayer(
    dikedPit.center,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );

  const dikedPitPadRaysLayer = constructRaysLayer(
    //Exterior Slope Breaks
    dikedPit.padRays,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Exterior Slope Breaks',
    elementId
  );
  const dikedPitPitRaysLayer = constructRaysLayer(
    //Interior Slope Breaks
    dikedPit.pitRays,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Interior Slope Breaks',
    elementId
  );

  const dikedPitPadEdgeLayer = constructDikedPitPadEdgeBreakLines(
    //Outer Top Edge
    dikedPit.padEdgeBreaklines,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Outer Top Edge',
    elementId
  );

  const layer = new GroupLayer();
  layer.title = dikedPit.name;

  if (dikedPitPadBorderLayer.graphics.length > 0) layer.add(dikedPitPadBorderLayer);
  if (dikedPitPadRaysLayer.graphics.length > 0) layer.add(dikedPitPadRaysLayer);
  if (dikedPitPadEdgeLayer.graphics.length > 0) layer.add(dikedPitPadEdgeLayer);
  if (dikedPitPitOuterBorderLayer.graphics.length > 0) layer.add(dikedPitPitOuterBorderLayer);
  if (dikedPitPitRaysLayer.graphics.length > 0) layer.add(dikedPitPitRaysLayer);
  if (dikedPitPitInnerBorderLayer.graphics.length > 0) layer.add(dikedPitPitInnerBorderLayer);
  if (slopeArrowsLayer.graphics.length > 0) layer.add(slopeArrowsLayer);
  if (centerPointLayer.graphics.length > 0) layer.add(centerPointLayer);
  if (label) {
    const dikedPitLabelPointsLayer = constructDikedPitLabelPointsLayer(
      label.center,
      label.pitRays,
      label.padRays,
      label.padVertices,
      label.cutSlopes,
      label.fillSlopes,
      label.slopeArrows,
      label.sideSlopes,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      fields
    );
    if (dikedPitLabelPointsLayer.source.length > 0) layer.add(dikedPitLabelPointsLayer);
  }

  return layer;
}

export function constructRoundPitLayer(
  roundPit: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  elementId,
  label?: any
): GroupLayer {

  const slopeArrowsLayer = constructSlopeArrowsLayerFromLineSegments(
    'Slope Arrows',
    roundPit.slopeArrows,
    __slopeArrowLineSymbol,
    inputSR,
    outputSR
  );

  const borderLinesLayer = constructBorderLinesLayer(
    roundPit.borders,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Exterior Border',
    elementId
  );

  const bottomLinesLayer = constructBottomLinesLayer(
    roundPit.vertices,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    elementId
  );

  const centerPointLayer = constructCenterPointLayer(
    roundPit.center,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );

  const layer = new GroupLayer();
  layer.title = roundPit.name;

  if (borderLinesLayer.graphics.length > 0) layer.add(borderLinesLayer);
  if (bottomLinesLayer.graphics.length > 0) layer.add(bottomLinesLayer);
  if (slopeArrowsLayer.graphics.length > 0) layer.add(slopeArrowsLayer);
  if (centerPointLayer.graphics.length > 0) layer.add(centerPointLayer);

  if (label) {
    const rectPitLabelPointsLayer = constructRoundPitLabelPointsLayer(
      label.center,
      label.circleNEWSPoints,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      fields
    );
    if (rectPitLabelPointsLayer.source.length > 0) layer.add(rectPitLabelPointsLayer);
  }
  return layer;
}

export function constructRectPitLayer(
  rectPit: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  elementId,
  label?: any
): GroupLayer {

  const slopeArrowsLayer = constructSlopeArrowsLayerFromLineSegments(
    'Slope Arrows',
    rectPit.slopeArrows,
    __slopeArrowLineSymbol,
    inputSR,
    outputSR
  );

  const borderLinesLayer = constructBorderLinesLayer(
    rectPit.borders,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Exterior Border',
    elementId
  );

  const raysLayer = constructRaysLayer(
    rectPit.rays,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Interior Slope Breaks',
    elementId
  );

  const bottomLinesLayer = constructBottomLinesLayer(
    rectPit.vertices,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    elementId
  );

  const centerPointLayer = constructCenterPointLayer(
    rectPit.center,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );

  const layer = new GroupLayer();
  layer.title = rectPit.name;

  if (borderLinesLayer.graphics.length > 0) { layer.add(borderLinesLayer) }
  if (raysLayer.graphics.length > 0) { layer.add(raysLayer) }
  if (bottomLinesLayer.graphics.length > 0) { layer.add(bottomLinesLayer) }
  if (slopeArrowsLayer.graphics.length > 0) { layer.add(slopeArrowsLayer) }
  if (centerPointLayer.graphics.length > 0) { layer.add(centerPointLayer) }

  if (label) {
    const rectPitLabelPointsLayer = constructRectPitLabelPointsLayer(
      label.center,
      label.rays,
      label.slopeArrows,
      label.slopes,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      fields
    );
    if (rectPitLabelPointsLayer.source.length > 0) { layer.add(rectPitLabelPointsLayer) }
  }

  return layer;
}

export function constructTerraceLayer(
  terrace: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  groupLayerId: string,
  title: string,
  alignedElementId: string,
): GroupLayer {
  const layers: Layer[] = [];
  const labelLayers: Layer[] = [];

  // Cross Section layer **************
  if (terrace.crossSectionLines && (terrace.crossSectionLines.length > 0)) {
    const crossSectLineLayer = constructMultiPolylineLayerFromInputPoints(
      'Cross Section',
      terrace.crossSectionLines,
      __trcCrossLineSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit
    );
    layers.push(crossSectLineLayer);
  }

  // Construction Edge layer (originally "Waterway Polygons") **************
  if (terrace.borderPolygon && (terrace.borderPolygon.length > 0)) {
    const borderPolygonsLayer = constructMultiplePolygonLayer(
      'Terrace Border',
      [terrace.borderPolygon],
      __trcBorderPolygonSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit
    );
    borderPolygonsLayer.id = 'terrace_polygon';
    layers.push(borderPolygonsLayer);

    const borderLabelsLayer = constructWaterwayPolygonLabelLayer(
      'borderPolygon_labels',
      'Terrace Border',
      borderPolygonsLayer,
      outputSR,
      __terraceLineLabels
    );
    labelLayers.push(borderLabelsLayer);
  }

  // Subbasin polygon
  if (terrace.subbasinPolygons && (terrace.subbasinPolygons.length > 0)) {
    const subbasinPolygonsLayer = constructWaterwayPolygonLayerL(
      'subbasinPolygon',
      'Subbasin',
      'Subbasin',
      terrace.subbasinPolygons,
      __trcSubbasinPolygonSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      alignedElementId
    );
    layers.push(subbasinPolygonsLayer[0]);
    labelLayers.push(subbasinPolygonsLayer[1]);
  }

  // Water level polygon
  if (terrace.waterLevelPolygon && (terrace.waterLevelPolygon.length > 0)) {
    const waterLevelLinesLayer = constructMultiplePolygonLayer(
      'Water Level',
      [terrace.waterLevelPolygon],
      __wwLevelPolygonSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      alignedElementId
    );
    layers.push(waterLevelLinesLayer);
  }

  // Front Cut Slope
  if (terrace.frontCutSlopePolygon && (terrace.frontCutSlopePolygon.length > 0)) {
    const frontCutSlopePolygonsLayer = constructWaterwayPolygonLayerL(
      'frontCutSlopePolygon',
      'Front Cut Slope',
      'Front Cut Slope',
      [terrace.frontCutSlopePolygon],
      __trcCutSlopePolygonSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      alignedElementId
    );
    layers.push(frontCutSlopePolygonsLayer[0]);
    labelLayers.push(frontCutSlopePolygonsLayer[1]);
  }

  // Back Cut Slope
  if (terrace.backCutSlopePolygon && (terrace.backCutSlopePolygon.length > 0)) {
    const backCutSlopePolygonsLayer = constructWaterwayPolygonLayerL(
      'backCutSlopePolygon',
      'Back Cut Slope',
      'Back Cut Slope',
      [terrace.backCutSlopePolygon],
      __trcCutSlopePolygonSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      alignedElementId
    );
    layers.push(backCutSlopePolygonsLayer[0]);
    labelLayers.push(backCutSlopePolygonsLayer[1]);
  }

  // Front Slope
  if (terrace.frontSlopePolygon && (terrace.frontSlopePolygon.length > 0)) {
    const frontSlopePolygonsLayer = constructWaterwayPolygonLayerL(
      'frontSlopePolygon',
      'Front Slope',
      'Front Slope',
      [terrace.frontSlopePolygon],
      __trcSlopePolygonSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      alignedElementId
    );
    layers.push(frontSlopePolygonsLayer[0]);
    labelLayers.push(frontSlopePolygonsLayer[1]);
  }

  // Back Slope
  if (terrace.backSlopePolygon && (terrace.backSlopePolygon.length > 0)) {
    const backSlopePolygonsLayer = constructWaterwayPolygonLayerL(
      'backSlopePolygon',
      'Back Slope',
      'Back Slope',
      [terrace.backSlopePolygon],
      __trcSlopePolygonSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      alignedElementId
    );
    layers.push(backSlopePolygonsLayer[0]);
    labelLayers.push(backSlopePolygonsLayer[1]);
  }

  // Ridge Top
  if (terrace.ridgeTopPolygon && (terrace.ridgeTopPolygon.length > 0)) {
    const ridgeTopPolygonsLayer = constructWaterwayPolygonLayerL(
      'ridgeTopPolygon',
      'Ridge Top',
      'Ridge Top',
      [terrace.ridgeTopPolygon],
      __trcRidgeTopPolygonSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      alignedElementId
    );
    layers.push(ridgeTopPolygonsLayer[0]);
    labelLayers.push(ridgeTopPolygonsLayer[1]);
  }

  // Channel bottom polygon
  if (terrace.bottomPolygon && (terrace.bottomPolygon.length > 0)) {
    const bottomPolygonsLayer = constructMultiplePolygonLayer(
      'Channel Bottom',
      [terrace.bottomPolygon],
      __trcBottomPolygonSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit
    );
    bottomPolygonsLayer.id = 'bottomPolygon';
    layers.push(bottomPolygonsLayer);

    const bottomLabelsLayer = constructWaterwayPolygonLabelLayer(
      'bottomPolygon_labels',
      'Channel Bottom',
      bottomPolygonsLayer,
      outputSR,
      __terraceLineLabels
    );
    labelLayers.push(bottomLabelsLayer);
  }

  // Channel Center Line
  if (terrace.bottomCenterLine && (terrace.bottomCenterLine.length > 0)) {
    const centerlineLayer = constructPolylineLayerFromInputPoints(
      'Channel Center Line',
      terrace.bottomCenterLine,
      __wwCenterLineSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      alignedElementId,
      false
    );
    layers.push(centerlineLayer);

    const centerlineLabelsLayer = constructWaterwayPolygonLabelLayer(
      'centerLine_labels',
      'Channel Center Line',
      centerlineLayer,
      outputSR,
      __terraceLineLabels
    );
    labelLayers.push(centerlineLabelsLayer);
  }

  // Flow Direction
  if (terrace.waterflowArrowsMap && (Object.keys(terrace.waterflowArrowsMap).length > 0)) {
    const flowDirLinesLayer = constructArrowPolylinesLayerFromArray(
      'Flow Direction',
      terrace.waterflowArrowsMap,
      __wwFlowArrowLineSymbol,
      // 33.33,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit
    );
    // const flowDirLinesLayer = constructFixedLengthPolylinesLayerFromArray(
    //   'Flow Direction',
    //   terrace.waterflowArrowsMap,
    //   __wwFlowArrowLineSymbol,
    //   33.33,
    //   inputSR,
    //   outputSR,
    //   inputElevationUnit,
    //   outputElevationUnit
    // );
    layers.push(flowDirLinesLayer);
  }

  // Slope Arrows
  if (terrace.slopeArrowsMap) {
    let arrows: any[] = [];
    const values = Object.values(terrace.slopeArrowsMap) as any[][];
    for (const a of values) {
      arrows = arrows.concat(a);
    }
    if (arrows.length > 0) {
      const slopeArrowsLinesLayer = construct3DSlopeArrowsLayerFromLineSegments(
        'Slope Direction',
        arrows,
        __wwSlopeArrowLineSymbol,
        inputSR,
        outputSR,
        inputElevationUnit,
        outputElevationUnit
      );
      layers.push(slopeArrowsLinesLayer);
    }
  }

  // Block Stations
  if (terrace.blockStations && (terrace.blockStations.length > 0)) {
    const blockStationLayer = constructPointLayerFromInputPoints(
      'Block Stations',
      terrace.blockStations,
      __blockStationMarkerSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit
    );
    layers.push(blockStationLayer);
  }

  // Drain Stations
  if (terrace.drainStations && (terrace.drainStations.length > 0)) {
    const drainStationLayer = constructPointLayerFromInputPoints(
      'Drain Stations',
      terrace.drainStations,
      __drainStationMarkerSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit
    );
    layers.push(drainStationLayer);
  }

  if (labelLayers.length > 0) {
    const labelLayerGroup = new GroupLayer({
      title: 'Terrace Labels',
      id: `${groupLayerId}_labels`,
      layers: labelLayers
    });
    layers.push(labelLayerGroup);
  }

  const resultLayer = new GroupLayer({
    title: title,
    id: groupLayerId,
    layers: layers
  });
  return resultLayer;
}

export function constructUgoLayer(
  ugoVertices: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  groupLayerId: string,
  title: string,
  alignedElementId: string,
): GroupLayer {
  const layers: Layer[] = [];
  const UgoLineLayer = constructPolylineLayerFromInputPoints(
    'UgoLayer',
    ugoVertices,
    __ugoLineSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    alignedElementId,
    false
  );
  layers.push(UgoLineLayer);
  const resultLayer = new GroupLayer({
    title: title,
    id: groupLayerId,
    layers: layers
  });
  return resultLayer;
}

export function constructWaterwayLayer(
  waterway: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  seedingBufferOption: number,
  groupLayerId: string,
  alignedElementId: string,
  title?: string
): GroupLayer {
  const layers: Layer[] = [];
  // Centerline, Start Slope, End Slope, Left Berm Edge, Right Berm Edge layers **************
  const breaklinesLabelLayers: any[] = [];
  const startSlopeLayers: any[] = [];
  const endSlopeLayers: any[] = [];
  const leftBermLayers: any[] = [];
  const leftBermLabelLayers: any[] = [];
  const rightBermLabelLayers: any[] = [];
  const rightBermLayers: any[] = [];

  if (waterway.breaklinesMap) {
    for (const [key, value] of Object.entries(waterway.breaklinesMap)) {
      for (const obj of value as BreaklineObject[]) {
        if (obj.breaklinePoints && obj.breaklinePoints.length > 0) {
          if (key !== "RightCorner" && key !== "LeftCorner" && key !== "RightBottom" && key !== "LeftBottom") {
            const label = `${key}`;
            if (label.trim() !== '') {
              const [breaklinesLayer, labelLayer] = constructBreaklinesLayerFromInputPoints(
                obj.breaklineID ? `${key}_${obj.breaklineID}` : `${key}`,
                label,
                obj.breaklinePoints,
                __wwBreaklinesSymbol,
                inputSR,
                outputSR,
                inputElevationUnit,
                outputElevationUnit
              );

              switch (true) {
                case key.toLowerCase().includes('startslope'):
                  startSlopeLayers.push(breaklinesLayer);
                  if (key.toLowerCase().includes('startslopecenterline')) {
                    breaklinesLabelLayers.push(labelLayer);
                  }
                  break;
                case key.toLowerCase().includes('endslope'):
                  endSlopeLayers.push(breaklinesLayer);
                  if (key.toLowerCase().includes('endslopecenterline')) {
                    breaklinesLabelLayers.push(labelLayer);
                  }
                  break;
                case key.toLowerCase().includes('leftotc'):
                  leftBermLayers.push(breaklinesLayer);
                  leftBermLabelLayers.push(labelLayer);
                  break;
                case key.toLowerCase().includes('rightotc'):
                  rightBermLayers.push(breaklinesLayer);
                  rightBermLabelLayers.push(labelLayer);
                  break;
                default:
                  layers.push(breaklinesLayer);
                  breaklinesLabelLayers.push(labelLayer);
                  break;
              }
            }
          }
        }
      }
    }
  }

  if (startSlopeLayers.length > 0) {
    const startSlopeLayer = new GroupLayer({
      title: "Start Slope",
      id: `start_slope${groupLayerId}`,
      layers: startSlopeLayers,
      listMode: 'hide-children'
    });
    layers.push(startSlopeLayer);
  }

  if (endSlopeLayers.length > 0) {
    const endSlopeLayer = new GroupLayer({
      title: "End Slope",
      id: `end_slope${groupLayerId}`,
      layers: endSlopeLayers,
      listMode: 'hide-children'
    });
    layers.push(endSlopeLayer);
  }

  if (leftBermLayers.length > 0) {
    const leftBermLayer = new GroupLayer({
      title: "Left Berm Edge",
      id: `left_berm_edge${groupLayerId}`,
      layers: leftBermLayers,
      listMode: 'hide-children'
    });
    layers.push(leftBermLayer);
  }

  if (rightBermLayers.length > 0) {
    const rightBermLayer = new GroupLayer({
      title: "Right Berm Edge",
      id: `right_berm_edge${groupLayerId}`,
      layers: rightBermLayers,
      listMode: 'hide-children'
    });
    layers.push(rightBermLayer);
  }

  if (leftBermLabelLayers.length > 0) {
    const leftBermLabelLayer = new GroupLayer({
      title: "Left Berm Edge",
      id: `left_berm_label${groupLayerId}`,
      layers: leftBermLabelLayers,
      listMode: 'hide-children'
    });
    breaklinesLabelLayers.push(leftBermLabelLayer);
  }

  if (rightBermLabelLayers.length > 0) {
    const rightBermLabelLayer = new GroupLayer({
      title: "Right Berm Edge",
      id: `right_berm_label${groupLayerId}`,
      layers: rightBermLabelLayers,
      listMode: 'hide-children'
    });
    breaklinesLabelLayers.push(rightBermLabelLayer);
  }

  // Slope Arrows layer **************
  const slopeArrowsLinesLayer = construct3DSlopeArrowsLayerFromLineSegments(
    'Slope',
    waterway.leftArrows.concat(waterway.rightArrows),
    __wwSlopeArrowLineSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );
  layers.push(slopeArrowsLinesLayer);

  // Water Level layer **************
  const waterLevelLinesLayer = constructMultiplePolygonLayer(
    'Water Level',
    [waterway.structurePolygons["WaterLevel"]],
    __wwLevelPolygonSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    alignedElementId
  );
  layers.push(waterLevelLinesLayer);

  // Channel Bottom layer **************
  const structurePolygonsLayer = constructStructurePolygonsLayer(
    'Channel Bottom',
    waterway.structurePolygons,
    __channelBottomSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    alignedElementId
  );
  layers.push(structurePolygonsLayer);

  // Flow Direction layer **************
  const flowDirLinesLayer = constructFixedLengthPolylinesLayerFromPairs(
    'Flow Direction',
    waterway.waterflowDirections,
    __wwFlowArrowLineSymbol,
    33.33,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );
  layers.push(flowDirLinesLayer);

  // Cross Section layers **************
  const crossPolygonLayer = constructMultiplePolygonLayer(
    'Cross Sections',
    waterway.crossPolygons,
    __wwCrossPolygonSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );
  layers.push(crossPolygonLayer);

  // Seeding Buffer layer **************
  if (waterway.seedingPolygons && (waterway.seedingPolygons.length > 0)) {

    const inputPolygonLayer = (seedingBufferOption === 1) ? waterway.borderPolygons[0] : waterway.structurePolygons['WaterLevel'];

    const seedingPolygonsLayer = constructSeedingPolygonAndBorderComboLayer(
      'seeding',
      'Seeding Buffer',
      'Seeding Buffer',
      waterway.seedingPolygons[0],
      inputPolygonLayer,
      __wwSeedingPolygonSymbol,
      __wwSeedingOutlineSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit
    );
    layers.push(seedingPolygonsLayer);
  }

  // Construction Edge layer (originally "Waterway Polygons") **************
  const borderPolygonsLayer = constructMultiplePolygonLayer(
    'Construction Edge',
    waterway.borderPolygons,
    __wwBorderPolygonSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );
  borderPolygonsLayer.id = 'waterway_polygon';
  layers.push(borderPolygonsLayer);

  // Labels layer **************
  const labelPointsLayer = constructWaterwayLabelPointsLayer(
    crossPolygonLayer,
    borderPolygonsLayer,
    breaklinesLabelLayers,
    waterway.seedingPolygons,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );
  layers.push(labelPointsLayer);

  const resultLayer = new GroupLayer({
    title: title || "Waterway",
    id: groupLayerId,
    layers: layers
  });
  return resultLayer;
}

export function constructPadLayer(
  pad: PadResponse,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  elementId,
  label?: any
): GroupLayer {
  const slopeArrowsLinesLayer = constructSlopeArrowsLayerFromLineSegments(
    'Slope Arrows',
    pad.slopeArrows,
    __slopeArrowLineSymbol,
    inputSR,
    outputSR
  );

  const center: InputPoint[] | undefined = (pad.center ? [pad.center] : undefined);
  const centerPointLayer = constructPointLayerFromInputPoints(
    'Center Point',
    center,
    __centerPointSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );

  const bottomLinesLayer = constructPolylineLayerFromInputPoints(
    'Top Border',
    pad.vertices,
    __structureLineSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    elementId
  );

  const bordersLineLayer = constructPolylineLayerFromInputPoints(
    'Exterior Border',
    pad.borders,
    __structureLineSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    elementId
  );

  const padRaysLayer = constructPolylinesLayerFromInputPointPairs(
    'Exterior Slope Breaks',
    pad.rays,
    __structureLineSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    elementId
  );

  const resultLayer = new GroupLayer({
    title: pad.name,
    layers: [bordersLineLayer, padRaysLayer, bottomLinesLayer, slopeArrowsLinesLayer, centerPointLayer],
  });

  if (label) {
    const padLabelPointsLayer = constructPadLabelPointsLayer(
      label.center,
      label.padRays,
      label.slopeArrows,
      label.cutSlopes,
      label.fillSlopes,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      fields,
    );
    if (padLabelPointsLayer.source.length > 0) resultLayer.add(padLabelPointsLayer);
  }

  return resultLayer;
}
function constructPointLayerFromInputPoints(
  title: string,
  inputPoints: InputPoint[] | undefined,
  pointSymbol: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;

  if (inputPoints) {
    for (const inputPoint of inputPoints) {
      const graphic = constructSinglePointGraphic(inputPoint.x!, inputPoint.y!, inputPoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit, pointSymbol);
      layer.add(graphic);
    }
  }

  return layer;
}

function constructBreaklinesLayerFromInputPoints(
  id: string,
  label: string,
  inputPoints: InputPoint[] | undefined,
  symbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
) {
  const graphics: Graphic[] = [];
  if (inputPoints) {
    switch (true) {
      case label === 'StartSlopeCenterline':
        label = 'Start Slope';
        break;
      case label === 'EndSlopeCenterline':
        label = 'End Slope';
        break;
      case label === 'LeftOTC':
        label = 'Left Berm Edge';
        break;
      case label === 'RightOTC':
        label = 'Right Berm Edge';
        break;
    }

    const polylines: Polyline[] = [];
    for (let i = 0; i < inputPoints.length - 1; i++) {
      const point1 = constructAndProject3dPoint(inputPoints[i].x!, inputPoints[i].y!, inputPoints[i].z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      const point2 = constructAndProject3dPoint(inputPoints[i + 1].x!, inputPoints[i + 1].y!, inputPoints[i + 1].z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);

      const polyline = new Polyline();
      polyline.spatialReference = outputSR;
      polyline.hasZ = true;
      polyline.addPath([point1, point2]);
      polylines.push(polyline);
    }
    const merged = union(polylines);
    const graphic = new Graphic({
      geometry: merged,
      symbol: symbol,
      attributes: { OBJECTID: 1, featureType: label },
    });
    graphics.push(graphic);
  }

  const layer = new FeatureLayer({
    id: id,
    title: label,
    spatialReference: outputSR,
    fields: [
      {
        name: 'OBJECTID',
        type: 'oid',
      },
      {
        name: 'featureType',
        type: 'string',
      },
    ],
    objectIdField: 'OBJECTID',
    displayField: 'featureType',
    hasZ: true,
    source: graphics,
    renderer: new SimpleRenderer({ symbol: symbol })
  });

  const labelLayer = new FeatureLayer({
    id: `${id}_label`,
    title: label,
    spatialReference: outputSR,
    fields: [
      {
        name: 'OBJECTID',
        type: 'oid',
      },
      {
        name: 'featureType',
        type: 'string',
      },
    ],
    objectIdField: 'OBJECTID',
    displayField: 'featureType',
    hasZ: true,
    source: graphics,
    renderer: new SimpleRenderer({ symbol: __hiddenLineSymbol }),
    labelingInfo: [__parallelLineLabels],
  });

  return [layer, labelLayer];
}


export function constructSlopeArrowsLayerFromLineSegments(
  title: string,
  lineSegments: LineSegmentDto[] | undefined,
  lineSymbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;

  if (lineSegments) {
    lineSegments.forEach((lineSegment, index) => {
      const point1 = constructPoint2dGeometry(lineSegment.startPoint!.x!, lineSegment.startPoint!.y!, inputSR);
      const point2 = constructPoint2dGeometry(lineSegment.endPoint!.x!, lineSegment.endPoint!.y!, inputSR);
      const path = [point1, point2];
      const polyline = new Polyline({ spatialReference: inputSR });
      polyline.hasZ = false;
      polyline.addPath(path);

      const graphic = new Graphic();
      graphic.geometry = esriProjection.project(polyline, outputSR) as Polyline;
      graphic.symbol = lineSymbol;
      layer.graphics.push(graphic);
    });
  }

  return layer;
}

export function construct3DSlopeArrowsLayerFromLineSegments(
  title: string,
  lineSegments: LineSegmentDto[] | undefined,
  lineSymbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;

  if (lineSegments) {
    lineSegments.forEach((lineSegment) => {
      const graphic = constructPolylineGraphicFrom3dPointPairs(lineSegment.startPoint!.x!, lineSegment.startPoint!.y!, lineSegment.startPoint!.z!, lineSegment.endPoint!.x!, lineSegment.endPoint!.y!, lineSegment.endPoint!.z!, lineSymbol, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      layer.graphics.push(graphic);
    });
  }

  return layer;
}

function constructPolylineLayerFromInputPoints(
  title: string,
  inputPoints: InputPoint[] | undefined,
  symbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  elementId,
  closeLoop: boolean = true
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (elementId) layer.id = `intersect_border-${elementId}`;
  if (inputPoints) {
    const points = inputPoints.map((inputPoint, index) => {
      return constructAndProject3dPoint(inputPoint.x!, inputPoint.y!, inputPoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
    });

    //close the border line
    if (closeLoop) {
      points.push(points[0].clone());
    }

    const polyline = new Polyline();
    polyline.spatialReference = outputSR;
    polyline.hasZ = true;
    polyline.addPath(points);

    //extent required for thumbnail
    if (title === 'Exterior Border') exteriorExtent = polyline.extent;

    const graphic = new Graphic();
    graphic.geometry = polyline;
    graphic.symbol = symbol;

    layer.add(graphic);
  }
  return layer;
}

export function constructMultiPolylineLayerFromInputPoints(
  title: string,
  inputPointsMany: InputPoint[][] | undefined,
  symbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (inputPointsMany) {
    const graphicsList = inputPointsMany.map(inputPoints => {
      const points = inputPoints.map((inputPoint, index) => {
        return constructAndProject3dPoint(inputPoint.x!, inputPoint.y!, inputPoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      });

      const polyline = new Polyline();
      polyline.spatialReference = outputSR;
      polyline.hasZ = true;
      polyline.addPath(points);

      const graphic = new Graphic();
      graphic.geometry = polyline;
      graphic.symbol = symbol;
      return graphic;
    });
    layer.addMany(graphicsList);
  }
  return layer;
}

function constructPolylinesLayerFromInputPointPairs(
  title: string,
  inputPointPairs: InputPoint[][] | undefined,
  symbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  elementId
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (elementId) layer.id = `intersect_border-${elementId}`;
  if (inputPointPairs) {
    const graphics: Graphic[] = [];
    const labels: Graphic[] = [];
    for (const inputPointPair of inputPointPairs) {
      const graphic = constructPolylineGraphicFrom3dPointPairs(inputPointPair[0].x!, inputPointPair[0].y!, inputPointPair[0].z!, inputPointPair[1].x!, inputPointPair[1].y!, inputPointPair[1].z!, symbol, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      graphics.push(graphic);
    }

    layer.addMany(graphics);
    layer.addMany(labels);
  }

  return layer;
}

function constructFixedLengthPolylinesLayerFromPairs(
  title: string,
  inputPointPairs: InputPoint[][] | undefined,
  symbol: SimpleLineSymbol,
  fixedLength: number,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (inputPointPairs) {
    const graphics = inputPointPairs.map((inputPointPair, index) => {
      let point1X = inputPointPair[0].x!;
      let point1Y = inputPointPair[0].y!;
      let point1Z = inputPointPair[0].z!;
      let point2X = inputPointPair[1].x!;
      let point2Y = inputPointPair[1].y!;
      let point2Z = inputPointPair[1].z!;

      let deltaX = point2X - point1X;
      let deltaY = point2Y - point1Y;
      const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (length > fixedLength) {
        const factor = fixedLength / length / 2.0;
        deltaX *= factor;
        deltaY *= factor;
        const deltaZ = (point2Z - point1Z) * factor;

        const midX = (point2X + point1X) / 2.0;
        const midY = (point2Y + point1Y) / 2.0;
        const midZ = (point2Z + point1Z) / 2.0;

        point1X = midX - deltaX;
        point2X = midX + deltaX;
        point1Y = midY - deltaY;
        point2Y = midY + deltaY;
        point1Z = midZ - deltaZ;
        point2Z = midZ + deltaZ;
      }

      const graphic = constructPolylineGraphicFrom3dPointPairs(point1X, point1Y, point1Z, point2X, point2Y, point2Z, symbol, inputSR, outputSR, inputElevationUnit, outputElevationUnit);

      return graphic;
    });

    layer.addMany(graphics);
  }

  return layer;
}

function constructArrowPolylinesLayerFromArray(
  title: string,
  inputArrows: any | undefined,
  symbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  layer.id = 'flowArrows';
  const graphics: Graphic[] = [];
  Object.values(inputArrows).forEach((stationArray: any) => {
    if (!Array.isArray(stationArray)) return;
    stationArray.forEach((arrow) => {
      const point1X = arrow.startPoint!.x;
      const point1Y = arrow.startPoint!.y;
      const point1Z = arrow.startPoint!.z;
      const point2X = arrow.endPoint!.x;
      const point2Y = arrow.endPoint!.y;
      const point2Z = arrow.endPoint!.z;

      const graphic = constructPolylineGraphicFrom3dPointPairs(point1X, point1Y, point1Z, point2X, point2Y, point2Z, symbol, inputSR, outputSR, inputElevationUnit, outputElevationUnit)
      graphics.push(graphic);
    });
  });
  layer.addMany(graphics);

  return layer;
}

function constructPolylineGraphicFromPointArray(points, outputSR: SpatialReference, symbol: SimpleLineSymbol, title?: string) {
  const polyline = new Polyline();
  polyline.spatialReference = outputSR;
  polyline.hasZ = true;
  polyline.addPath(points);

  //extent required for thumbnail
  if (title && title === 'Exterior Border') exteriorExtent = polyline.extent;

  const graphic = new Graphic();
  graphic.geometry = polyline;
  graphic.symbol = symbol;

  return graphic;
}

function constructPolylineGraphicFrom3dPointPairs(
  point1X,
  point1Y,
  point1Z,
  point2X,
  point2Y,
  point2Z,
  symbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
) {

  const point1 = constructAndProject3dPoint(point1X, point1Y, point1Z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
  const point2 = constructAndProject3dPoint(point2X, point2Y, point2Z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);

  const polyline = new Polyline();
  polyline.spatialReference = outputSR;
  polyline.hasZ = true;
  polyline.addPath([point1, point2]);

  const graphic = new Graphic();
  graphic.geometry = polyline;
  graphic.symbol = symbol;

  return graphic;
}

export function constructWaterwayLabelPointsLayer(
  crossPolygons: GraphicsLayer,
  waterwayPolygons: GraphicsLayer,
  breaklineLayers: any[],
  seedingPoints: any[] | undefined,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
) {

  const labelLayers = [] as any[];
  // Breakline layers
  if (breaklineLayers) {
    breaklineLayers.forEach((lyr) => {
      labelLayers.push(lyr);
    })
  }

  // Cross section polygons
  if (crossPolygons) {
    const pointSource = [] as Graphic[];
    crossPolygons.graphics.forEach((p) => {
      const label = labelPolygonAtCentroid(p.geometry as Polygon, { text: 'Cross Section' });
      if (label) {
        pointSource.push(label);
      }
    });
    const crossPolygonLayer = new GraphicsLayer({
      title: 'Cross Sections',
      id: 'Cross_section_labels'
    });
    crossPolygonLayer.addMany(pointSource);
    labelLayers.push(crossPolygonLayer);
  }

  // Seeding polygon
  if (seedingPoints && seedingPoints[0]) {
    const labelLayer = constructPolylineFLayerFromInputPoints(
      'seeding_label',
      'Seeding Buffer',
      'Seeding Buffer',
      seedingPoints[0] as InputPoint[],
      __hiddenLineSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit,
      true
    );
    labelLayers.push(labelLayer);
  }


  // Waterway polygons
  if (waterwayPolygons) {
    const wwPolygonsLayer = constructWaterwayPolygonLabelLayer('Waterway_polygon', 'Construction Edge', waterwayPolygons, outputSR);
    labelLayers.push(wwPolygonsLayer);
  }

  const resultLabelLayer = new GroupLayer({
    title: 'Labels',
    layers: labelLayers,
  });

  return resultLabelLayer;
}

let exteriorExtent;
function constructBorderLinesLayer(
  borders: any[],
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  title: string,
  elementId
): GraphicsLayer {
  const layer = new GraphicsLayer();
  if (elementId) layer.id = `intersect_border-${elementId}`;
  layer.title = title;
  if (borders.length > 0) {
    const points = borders.map((value, index) => {
      const point = constructAndProject3dPoint(value.x, value.y, value.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      return point;
    });

    //close the border line
    points.push(points[0].clone());

    const graphic = constructPolylineGraphicFromPointArray(points, outputSR, __structureLineSymbol, title);

    layer.add(graphic);
  }
  return layer;
}

function constructDikedPitPadEdgeBreakLines(
  breakLinesBorderPts: any[],
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  title: string,
  elementId
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (elementId) layer.id = `intersect_border-${elementId}`;

  if (breakLinesBorderPts.length > 0) {
    for (let idx = 0; idx < breakLinesBorderPts.length - 1; idx++) {
      const graphic = constructPolylineGraphicFrom3dPointPairs(
        breakLinesBorderPts[idx][0].x,
        breakLinesBorderPts[idx][0].y,
        breakLinesBorderPts[idx][0].z,
        breakLinesBorderPts[idx + 1][0].x,
        breakLinesBorderPts[idx + 1][0].y,
        breakLinesBorderPts[idx + 1][0].z,
        __structureLineSymbol,
        inputSR,
        outputSR,
        inputElevationUnit,
        outputElevationUnit
      )

      layer.graphics.push(graphic);
    }

    const graphic = constructPolylineGraphicFrom3dPointPairs(
      breakLinesBorderPts[0][0].x,
      breakLinesBorderPts[0][0].y,
      breakLinesBorderPts[0][0].z,
      breakLinesBorderPts[breakLinesBorderPts.length - 1][0].x,
      breakLinesBorderPts[breakLinesBorderPts.length - 1][0].y,
      breakLinesBorderPts[breakLinesBorderPts.length - 1][0].z,
      __structureLineSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit
    )

    layer.graphics.push(graphic);
  }

  return layer;
}

function constructRaysLayer(
  rays: any[],
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  title: string,
  elementId
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (elementId) layer.id = `intersect_border-${elementId}`;

  rays.forEach((value, index) => {
    const graphic = constructPolylineGraphicFrom3dPointPairs(
      value[0].x,
      value[0].y,
      value[0].z,
      value[1].x,
      value[1].y,
      value[1].z,
      __structureLineSymbol,
      inputSR,
      outputSR,
      inputElevationUnit,
      outputElevationUnit
    )

    layer.graphics.push(graphic);
  });

  return layer;
}

function constructBottomLinesLayer(
  vertices: any[],
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  elementId
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = 'Bottom Border';
  if (elementId) layer.id = `intersect_border-${elementId}`;

  if (vertices.length > 0) {
    const points = vertices.map((value, index) => {
      const point = constructAndProject3dPoint(value.x, value.y, value.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      return point;
    });

    //close the border line
    points.push(points[0].clone());

    const graphic = constructPolylineGraphicFromPointArray(points, outputSR, __structureLineSymbol);

    layer.add(graphic);
  }
  return layer;
}

function constructCenterPointLayer(
  center: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = 'Center Point';
  if (center) {
    const graphic = constructSinglePointGraphic(center.x, center.y, center.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit, __centerPointSymbol);
    layer.add(graphic);
  }
  return layer;
}

function constructRectPitLabelPointsLayer(
  centerData: any,
  raysData: any,
  slopeArrowsData: any,
  slopesData: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  fields
): FeatureLayer {

  const center = centerData ? JSON.parse(centerData) : undefined;
  const rays = raysData ? JSON.parse(raysData) : undefined;
  const arrows = slopeArrowsData ? JSON.parse(slopeArrowsData) : undefined;
  const slopes = slopesData ? JSON.parse(slopesData) : undefined;

  const pointSource = [] as Graphic[];

  // Center point
  if (center) {
    const point = constructAndProject3dPoint(center.x, center.y, center.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
    const graphic = constructStructureLabelPointGraphic(point, `${center.pointName}`, 'CP', 'above-center');
    pointSource.push(graphic);
  }

  // Bottom & Boundary points
  if (rays) {
    rays.map((points) => {
      points.map((value, index) => {
        const point = constructAndProject3dPoint(value.x, value.y, value.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
        const graphic = constructStructureLabelPointGraphic(point, `${value.pointName}`, 'BC', defineLabelPosition(center.x, center.y, value.x, value.y));
        pointSource.push(graphic);
      });
    });
  }

  // Arrow points
  if (arrows && slopes) {
    arrows.map((value, index) => {
      const pointStart = value.startPoint;
      const pointEnd = value.endPoint;
      const point = constructPoint2dGeometry((pointStart.x + pointEnd.x) / 2, (pointStart.y + pointEnd.y) / 2, inputSR);
      const labelName = findMatchedLabel(slopes, pointStart);
      const graphic = constructStructureLabelPointGraphic(point, labelName, 'SS', 'below-center');

      pointSource.push(graphic);
    });
  }

  const profileLabelPointRenderer = {
    type: "unique-value",
    field: "Type",
    uniqueValueInfos: [
      createFillSymbol("CP", "Transparent", 0),
      createFillSymbol("BC", [66, 135, 245], 1),
      createFillSymbol("SS", "Transparent", 0)
    ]
  };

  return createLabelPointLayer(pointSource, fields, profileLabelPointRenderer);
}

function constructRoundPitLabelPointsLayer(
  centerData: any,
  raysData: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  fields
): FeatureLayer {

  const center = centerData ? JSON.parse(centerData) : undefined;
  const rays = raysData.map(jsonString => JSON.parse(jsonString));


  const pointSource = [] as Graphic[];

  // Center point
  if (center) {
    const point = constructAndProject3dPoint(center.x, center.y, center.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
    const graphic = constructStructureLabelPointGraphic(point, `${center.pointName}`, 'CP', 'above-center');
    pointSource.push(graphic);
  }

  // Bottom & Boundary points
  if (rays) {
    rays.map((value, index) => {
      const point = constructAndProject3dPoint(value.x, value.y, value.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      const graphic = constructStructureLabelPointGraphic(point, `${value.pointName}`, 'BC', defineLabelPosition(center.x, center.y, value.x, value.y));
      pointSource.push(graphic);
    });
  }

  const profileLabelPointRenderer = {
    type: "unique-value",
    field: "Type",
    uniqueValueInfos: [
      createFillSymbol("CP", "Transparent", 0),
      createFillSymbol("BC", [66, 135, 245], 1),
      createFillSymbol("SS", "Transparent", 0)
    ]
  };

  return createLabelPointLayer(pointSource, fields, profileLabelPointRenderer);
}

function constructPadLabelPointsLayer(
  centerData: any,
  padRaysData: any,
  slopeArrowsData: any,
  cutSlopesData: any,
  fillSlopesData: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  fields
): FeatureLayer {

  const center = centerData ? JSON.parse(centerData) : undefined;
  const rays = padRaysData ? JSON.parse(padRaysData) : undefined;
  const arrows = slopeArrowsData ? JSON.parse(slopeArrowsData) : undefined;
  const cutSlopes = cutSlopesData ? JSON.parse(cutSlopesData) : undefined;
  const fillSlopes = fillSlopesData ? JSON.parse(fillSlopesData) : undefined;

  const pointSource = [] as Graphic[];

  // Center point
  if (center) {
    const point = constructAndProject3dPoint(center.x, center.y, center.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
    const graphic = constructStructureLabelPointGraphic(point, `${center.pointName}`, 'CP', 'above-center');
    pointSource.push(graphic);
  }

  // Bottom & Boundary point
  if (rays) {
    rays[0].map((value, index) => {
      const point = constructAndProject3dPoint(value.x, value.y, value.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      const graphic = constructStructureLabelPointGraphic(point, `${value.pointName}`, 'TC', defineLabelPosition(center.x, center.y, value.x, value.y));
      pointSource.push(graphic);
    });
  }

  // Arrow points
  if (arrows && cutSlopes && fillSlopes) {
    for (let i = 0; i < cutSlopes.length; i++) {
      cutSlopes[i].slopeName = fillSlopes[i].slopeName.substr(0, 2) + '/' + cutSlopes[i].slopeName;
    }
    arrows.map((value, index) => {
      const pointStart = value.startPoint;
      const pointEnd = value.endPoint;
      let pointSS = constructPoint2dGeometry((pointStart.x + pointEnd.x) / 2, (pointStart.y + pointEnd.y) / 2, inputSR);
      pointSS = esriProjection.project(pointSS, outputSR) as Point;

      const labelName = findMatchedLabel(cutSlopes, pointStart);

      const graphicSS = new Graphic({
        geometry: new Point({
          spatialReference: outputSR,
          x: pointSS.x,
          y: pointSS.y,
        }),
        attributes: {
          Label: labelName,
          Type: 'FCS',
          Placement: 'below-center',
        },
      });

      pointSource.push(graphicSS);
    });
  }

  const profileLabelPointRenderer = {
    type: "unique-value",
    field: "Type",
    uniqueValueInfos: [
      createFillSymbol("CP", "Transparent", 0),
      createFillSymbol("TC", [66, 135, 245], 1),
      createFillSymbol("FCS", "Transparent", 0)
    ]
  };

  return createLabelPointLayer(pointSource, fields, profileLabelPointRenderer);
}

const fields = [
  {
    name: 'ObjectID',
    alias: 'ObjectID',
    type: 'oid',
  },
  {
    name: 'Label',
    alias: 'Label',
    type: 'string',
  },
  {
    name: 'Type',
    alias: 'Type',
    type: 'string',
  },
  {
    name: 'Placement',
    alias: 'Placement',
    type: 'string',
  },
];

function findMatchedLabel(slopePoints, arrowPoint) {
  let minDist: number = (slopePoints[0].x - arrowPoint.x) ** 2 + (slopePoints[0].y - arrowPoint.y) ** 2;
  let label: string = slopePoints[0].slopeName;
  for (let i = 1; i < slopePoints.length; i++) {
    if (((slopePoints[i].x - arrowPoint.x) ** 2 + (slopePoints[i].y - arrowPoint.y) ** 2) < minDist) {
      minDist = (slopePoints[i].x - arrowPoint.x) ** 2 + (slopePoints[i].y - arrowPoint.y) ** 2;
      label = slopePoints[i].slopeName
    }
  }
  return label;
}

function defineLabelPosition(centerX, centerY, x, y) {
  if (x < centerX && y >= centerY) return 'above-left';
  else if (x < centerX && y < centerY) return 'below-left';
  else if (x >= centerX && y < centerY) return 'below-right';
  else return 'above-right';
}

function constructDikedPitLabelPointsLayer(
  centerData: any,
  pitRaysData: any,
  padRaysData: any,
  padVerticesData: any,
  cutSlopesLabel: any,
  fillSlopesLabel: any,
  slopeArrowsData: any,
  sideSlopesLabel: any,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  fields
): FeatureLayer {

  const center = centerData ? JSON.parse(centerData) : undefined;
  const pitRays = pitRaysData ? JSON.parse(pitRaysData) : undefined;
  const padRays = padRaysData ? JSON.parse(padRaysData) : undefined;
  const padVertices = padVerticesData ? JSON.parse(padVerticesData) : undefined;
  const cutLabels = cutSlopesLabel ? JSON.parse(cutSlopesLabel) : undefined;
  const fillLabels = fillSlopesLabel ? JSON.parse(fillSlopesLabel) : undefined;
  const sideLabels = sideSlopesLabel ? JSON.parse(sideSlopesLabel) : undefined;
  const arrows = slopeArrowsData ? JSON.parse(slopeArrowsData) : undefined;

  const pointSource = [] as Graphic[];

  // Center point
  if (center) {
    let pointC = constructPoint3dGeometry(center.x, center.y, center.z, inputSR);
    pointC = esriProjection.project(pointC, outputSR) as Point;
    pointC.z = convertLinearUnit(pointC.z, inputElevationUnit, outputElevationUnit);

    const graphicC = new Graphic();
    graphicC.geometry = pointC;
    graphicC.attributes = {
      Label: `${center.pointName}`,
      Type: 'CP',
      Placement: 'above-center',
    }
    pointSource.push(graphicC);

    // Bottom & Boundary points
    if (padRays) {
      padRays[0].map((item) => {
        const point = constructAndProject3dPoint(item.x, item.y, item.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);

        const points = new Graphic({
          geometry: new Point({
            spatialReference: outputSR,
            x: point.x,
            y: point.y,
          }),
          attributes: {
            Label: `${item.pointName}`,
            Type: 'C',
            Placement: defineLabelPosition(center.x, center.y, item.x, item.y),
          },
        });

        pointSource.push(points);
      });
    }
  }

  if (padVertices) {
    padVertices[0].map((item) => {
      const point = constructAndProject3dPoint(item.x, item.y, item.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);

      const points = new Graphic({
        geometry: new Point({
          spatialReference: outputSR,
          x: point.x,
          y: point.y,
        }),
        attributes: {
          Label: `${item.pointName}`,
          Type: 'OT',
          Placement: defineLabelPosition(center.x, center.y, item.x, item.y),
        },
      });

      pointSource.push(points);
    });
  }

  if (pitRays) {
    pitRays.map((points) => {
      points.map((item) => {
        const point = constructAndProject3dPoint(item.x, item.y, item.z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);

        const points = new Graphic({
          geometry: new Point({
            spatialReference: outputSR,
            x: point.x,
            y: point.y,
          }),
          attributes: {
            Label: `${item.pointName}`,
            Type: 'ITB',
            Placement: defineLabelPosition(center.x, center.y, item.x, item.y),
          },
        });

        pointSource.push(points);
      });
    });
  }

  // Slope Arrows
  if (arrows && cutLabels && fillLabels && sideLabels) {

    for (let i = 0; i < cutLabels.length; i++) {
      cutLabels[i].slopeName = fillLabels[i].slopeName.substr(0, 2) + '/' + cutLabels[i].slopeName;
    }
    cutLabels.forEach((item) => {
      sideLabels.push(item);
    })

    arrows.map((value, index) => {
      const pointStart = value.startPoint;
      const pointEnd = value.endPoint;
      let points = constructPoint2dGeometry((pointStart.x + pointEnd.x) / 2, (pointStart.y + pointEnd.y) / 2, inputSR);
      points = esriProjection.project(points, outputSR) as Point;

      const labelName = findMatchedLabel(sideLabels, pointEnd);

      const graphics = new Graphic({
        geometry: new Point({
          spatialReference: outputSR,
          x: points.x,
          y: points.y,
        }),
        attributes: {
          Label: labelName,
          Type: 'S',
          Placement: 'below-center',
        },
      });

      pointSource.push(graphics);
    });
  }

  const profileLabelPointRenderer = {
    type: "unique-value",
    field: "Type",
    uniqueValueInfos: [
      createFillSymbol("CP", "Transparent", 0),
      createFillSymbol("ITB", [66, 135, 245], 1),
      createFillSymbol("OT", [66, 135, 245], 1),
      createFillSymbol("C", [66, 135, 245], 1),
      createFillSymbol("S", "Transparent", 0)
    ]
  };

  return createLabelPointLayer(pointSource, fields, profileLabelPointRenderer);
}

function createFillSymbol(value, color, width) {
  return {
    "value": value,
    "symbol": {
      "color": color,
      "type": "simple-marker",
      "size": 6,
      "outline": {
        "style": 'solid',
        "color": [0, 0, 0],
        "width": width,
      },
    },
  };
}

function createLabelSymbol(x, y) {
  return {
    type: 'text',
    color: 'black',
    font: {
      family: 'Ubuntu Mono',
      size: 11,
    },
    xoffset: x,
    yoffset: y,
  };
}

function createLabelPointLayer(pointSource: Graphic[], fields, profileLabelPointRenderer): FeatureLayer {
  const aboveCenterClass = {
    labelExpressionInfo: {
      expression: '$feature.Label'
    },
    deconflictionStrategy: 'none',
    labelPlacement: 'above-center',
    where: "Placement = 'above-center'",
    symbol: createLabelSymbol(0, -3) // make the label of center point closer to the point to save space for other labels
  };

  const belowCenterClass = {
    labelExpressionInfo: {
      expression: '$feature.Label'
    },
    labelPlacement: 'below-center',
    where: "Placement = 'below-center'",
    symbol: createLabelSymbol(0, 13) // make the label of slopes on top of the arrow points
  };

  const aboveRightClass = {
    labelExpressionInfo: {
      expression: '$feature.Label'
    },
    labelPlacement: 'above-right',
    where: "Placement = 'above-right'",
    symbol: createLabelSymbol(0, 0)
  };

  const aboveLeftClass = {
    labelExpressionInfo: {
      expression: '$feature.Label'
    },
    labelPlacement: 'above-left',
    where: "Placement = 'above-left'",
    symbol: createLabelSymbol(0, 0)
  };

  const belowRightClass = {
    labelExpressionInfo: {
      expression: '$feature.Label'
    },
    labelPlacement: 'below-right',
    where: "Placement = 'below-right'",
    symbol: createLabelSymbol(0, 0)
  };

  const belowLeftClass = {
    labelExpressionInfo: {
      expression: '$feature.Label'
    },
    labelPlacement: 'below-left',
    where: "Placement = 'below-left'",
    symbol: createLabelSymbol(0, 0)
  };

  const labelPointLayer = new FeatureLayer({
    source: pointSource,
    fields,
    renderer: profileLabelPointRenderer as any,
    id: 'label_points',
    title: 'LabelPoints',
    objectIdField: 'ObjectID',
    labelingInfo: [
      aboveCenterClass,
      belowCenterClass,
      aboveRightClass,
      aboveLeftClass,
      belowRightClass,
      belowLeftClass,
    ] as any
  });
  return labelPointLayer;
}

export function constructPoint2dGeometry(x: number, y: number, inputSR: SpatialReference): Point {
  const point = new Point();
  point.spatialReference = inputSR;
  point.x = x;
  point.y = y;
  point.hasZ = false;
  return point;
}

export function constructPoint3dGeometry(x: number, y: number, z: number, inputSR: SpatialReference): Point {
  const point = new Point();
  point.spatialReference = inputSR;
  point.x = x;
  point.y = y;
  point.z = z;
  point.hasZ = true;
  return point;
}

function constructAndProject3dPoint(x, y, z, inputSR: SpatialReference, outputSR: SpatialReference, inputElevationUnit: LinearUnit, outputElevationUnit: LinearUnit): Point {
  let point = constructPoint3dGeometry(x, y, z, inputSR);
  point = esriProjection.project(point, outputSR) as Point;
  point.z = convertLinearUnit(point.z, inputElevationUnit, outputElevationUnit);

  return point;
}

function constructSinglePointGraphic(x, y, z, inputSR: SpatialReference, outputSR: SpatialReference, inputElevationUnit: LinearUnit, outputElevationUnit: LinearUnit, symbol) {
  const point = constructAndProject3dPoint(x, y, z, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
  const graphic = new Graphic();
  graphic.geometry = point;
  graphic.symbol = symbol;

  return graphic;
}

function constructStructureLabelPointGraphic(point: Point, label, type, placement): Graphic {
  const graphic = new Graphic();
  graphic.geometry = point;
  graphic.attributes = {
    Label: label,
    Type: type,
    Placement: placement,
  }

  return graphic;
}

export function convertLinearUnit(value: number, inputUnit: LinearUnit, outputUnit: LinearUnit): number {
  if (inputUnit === outputUnit) {
    return value;
  }

  const usFeetToInternationlFeet = 1.000002000004000008000016000032;
  const metersToInternationlFeet = 3.280839895;
  const metersToUsFeet = 3.2808333333;

  if (inputUnit === 'intl-feet') {
    if (outputUnit === 'meters') {
      return value / metersToInternationlFeet;
    } else if (outputUnit === 'us-feet') {
      return value / usFeetToInternationlFeet;
    } else {
      throw new Error('Unsupported unit conversion.');
    }
  } else if (inputUnit === 'meters') {
    if (outputUnit === 'intl-feet') {
      return value * metersToInternationlFeet;
    } else if (outputUnit === 'us-feet') {
      return value * metersToUsFeet;
    } else {
      throw new Error('Unsupported conversion.');
    }
  } else if (inputUnit === 'us-feet') {
    if (outputUnit === 'meters') {
      return value / metersToUsFeet;
    } else if (outputUnit === 'intl-feet') {
      return value * usFeetToInternationlFeet;
    } else {
      throw new Error('Unsupported unit conversion.');
    }
  } else {
    throw new Error('Unsupported unit conversion.');
  }
}

export function constructLandLevelingLayer(
  landLeveling: LandLevelingResponse,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  elementId
): GroupLayer {
  // TO DO: Create Buffer Boundary Layer
  const bufferPolygonLayer = constructLandLevelingBufferPolygonLayer(
    'Buffer Polygon',
    landLeveling.bufferBorder,
    __landLevelingFillSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );

  const boundaryLayer = constructLandLevelingBoundaryLayer(
    'Exterior Border',
    landLeveling.boundaryPoints,
    landLeveling.modifiedPlanePoints,
    __structureLineSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    elementId
  );

  const boundaryPolygonLayer = constructLandLevelingPlaneLayer(
    'Plane',
    landLeveling.boundaryPoints,
    landLeveling.modifiedPlanePoints,
    __landLevelingFillSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );

  const stakeoutPointsLayer = constructLandLevelingStakeoutGridPointsLayer(
    'Stakeout Grid Points',
    landLeveling.stakeOutPoints,
    __landLevelingStakeoutGridPointsSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );

  const planePointsLayer = constructLandLevelingPlanePointsLayer(
    landLeveling,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    'Plane Points'
  );

  const slopeArrowsLayer = constructSlopeArrowsLayerFromLineSegments(
    'Slope Arrows',
    landLeveling.slopeArrows,
    __landLevelingSlopeArrowLineSymbol,
    inputSR,
    outputSR
  );

  const basePointLayer = constructLandLevelingBasePointLayer(
    'Base Point',
    landLeveling.basePoint,
    __centerPointSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );

  const resultLayer = new GroupLayer({
    title: landLeveling.name,
    layers: [bufferPolygonLayer, boundaryPolygonLayer, boundaryLayer, stakeoutPointsLayer, planePointsLayer, slopeArrowsLayer, basePointLayer],
  });

  return resultLayer;
}

function constructLandLevelingBoundaryLayer(
  title: string,
  boundaryPoints: InputPoint[] | undefined,
  modifiedPlanePoints: InputPoint[] | undefined,
  lineSymbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  elementId
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (elementId) layer.id = `intersect_border-${elementId}`;

  const points: Point[] = [];

  if (boundaryPoints && modifiedPlanePoints) {
    if (modifiedPlanePoints && modifiedPlanePoints.length) {
      boundaryPoints.forEach((boundaryPoint) => {
        modifiedPlanePoints.forEach((planePoint) => {
          // if x and y match then set z
          if (planePoint.x === boundaryPoint.x && planePoint.y === boundaryPoint.y) {
            // boundaryPoint.y = planePoint.z! * 0.3048; // This converts from feet to meters
            const point = constructAndProject3dPoint(planePoint.x!, planePoint.y!, planePoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
            points.push(point);
          }
        });
      });
    } else {
      boundaryPoints.forEach((boundaryPoint) => {
        const point = constructAndProject3dPoint(boundaryPoint.x!, boundaryPoint.y!, boundaryPoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
        points.push(point);
      });
    }
    //close the border line
    points.push(points[0].clone());

    const graphic = constructPolylineGraphicFromPointArray(points, outputSR, lineSymbol);
    exteriorExtent = graphic.geometry.extent;

    layer.add(graphic);
  }

  return layer;
}

function constructLandLevelingPlaneLayer(
  title: string,
  boundaryPoints: InputPoint[] | undefined,
  modifiedPlanePoints: InputPoint[] | undefined,
  fillSymbol: SimpleFillSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;

  const points: Point[] = [];

  if (boundaryPoints && modifiedPlanePoints) {
    if (modifiedPlanePoints && modifiedPlanePoints.length) {
      boundaryPoints.forEach((boundaryPoint) => {
        modifiedPlanePoints.forEach((planePoint) => {
          // if x and y match then set z
          if (planePoint.x === boundaryPoint.x && planePoint.y === boundaryPoint.y) {
            // boundaryPoint.y = planePoint.z! * 0.3048; // This converts from feet to meters
            const point = constructAndProject3dPoint(planePoint.x!, planePoint.y!, planePoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
            points.push(point);
          }
        });
      });
    } else {
      boundaryPoints.forEach((boundaryPoint) => {
        const point = constructAndProject3dPoint(boundaryPoint.x!, boundaryPoint.y!, boundaryPoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
        points.push(point);
      });
    }

    //close the border line
    points.push(points[0].clone());

    const graphic = constructPolygonGraphicFromEsriPoints(outputSR, fillSymbol, points);
    layer.add(graphic);
  }

  return layer;
}

function constructLandLevelingBufferPolygonLayer(
  title: string,
  bufferPoints: InputPoint[] | undefined,
  fillSymbol: SimpleFillSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;

  const points: Point[] = [];
  if (bufferPoints) {
    bufferPoints.forEach((bufferPoint) => {
      const point = constructAndProject3dPoint(bufferPoint.x!, bufferPoint.y!, bufferPoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      points.push(point);

    });

    //close the border polygon
    points.push(points[0].clone());

    // create new polygon
    const graphic = constructPolygonGraphicFromEsriPoints(outputSR, fillSymbol, points);
    layer.add(graphic);
  }

  return layer;
}

export function constructSeedingPolygonLayer(
  title: string,
  outerPoints: InputPoint[] | undefined,
  innerPoints: InputPoint[] | undefined,
  fillSymbol: SimpleFillSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;

  if (outerPoints && innerPoints) {
    // find the point in the outer ring closest to the beginning of the inner ring
    let index = -1;
    let minDistance = Number.MAX_VALUE;
    for (let i = 0; i < outerPoints.length; i++) {
      const dx = innerPoints[0].x! - outerPoints[i].x!;
      const dy = innerPoints[0].y! - outerPoints[i].y!;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < minDistance) {
        index = i;
        minDistance = dist;
      }
    }

    if (index > -1) {
      const points: InputPoint[] = [];
      for (let i = index; i < outerPoints.length; i++) {
        points.push(outerPoints[i]);
      }
      for (let i = 0; i <= index; i++) {
        points.push(outerPoints[i]);
      }
      for (let i = innerPoints.length - 1; i >= 0; i--) {
        points.push(innerPoints[i]);
      }
      points.push(outerPoints[index]);

      const geometry = constructPolygonFromInputPoints(
        points,
        inputSR,
        outputSR,
        inputElevationUnit,
        outputElevationUnit
      );

      const graphic = new Graphic();
      graphic.geometry = geometry;
      graphic.symbol = fillSymbol;
      layer.add(graphic);
    }
  }
  return layer;
}

function constructPolylineFLayerFromInputPoints(
  id: string,
  title: string,
  label: string,
  inputPoints: InputPoint[] | undefined,
  symbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  addLabels: boolean = false
): FeatureLayer {
  const graphics: Graphic[] = [];

  if (inputPoints) {
    const points = inputPoints.map((inputPoint, index) => {
      return constructAndProject3dPoint(inputPoint.x!, inputPoint.y!, inputPoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
    });

    const graphic = constructPolylineGraphicFromPointArray(points, outputSR, symbol);
    graphic.attributes = { OBJECTID: 1, featureType: label };

    graphics.push(graphic);
  }

  const labelInfo = addLabels ? [__polygonBorderLabels] : [];

  const labelLayer = new FeatureLayer({
    id: id,
    title: title,
    spatialReference: outputSR,
    fields: [
      {
        name: 'OBJECTID',
        type: 'oid',
      },
      {
        name: 'featureType',
        type: 'string',
      },
    ],
    objectIdField: 'OBJECTID',
    displayField: 'featureType',
    hasZ: true,
    source: graphics,
    renderer: new SimpleRenderer({ symbol: symbol }),
    labelingInfo: labelInfo
  });
  return labelLayer;
}

export function constructSeedingPolygonAndBorderComboLayer(
  id: string,
  title: string,
  label: string,
  outerPoints: InputPoint[] | undefined,
  innerPoints: InputPoint[] | undefined,
  fillSymbol: SimpleFillSymbol,
  lineSymbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GroupLayer {
  const polygonLayer = constructSeedingPolygonLayer(
    title,
    outerPoints,
    innerPoints,
    fillSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );
  polygonLayer.id = `${id}_polygon`;

  const outlineLayer = constructPolylineFLayerFromInputPoints(
    `${id}_outline`,
    `${title} Outline`,
    label,
    outerPoints,
    lineSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit
  );

  const resultLayer = new GroupLayer({
    title: title,
    id: `${id}_combo`,
    layers: [polygonLayer, outlineLayer],
    listMode: 'hide-children',
  });
  return resultLayer;
}

export function constructStructurePolygonsLayer(
  title: string,
  structurePolygons: { [key: string]: InputPoint[] },
  fillSymbol: SimpleLineSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  alignedElementId,
  labelFunc: ((poly: Polygon, params: any) => Graphic) | undefined = undefined,
  labelParams: any = undefined
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (alignedElementId) layer.id = `intersect_border-${alignedElementId}`;

  const structurePolygonsKeys = Object.keys(structurePolygons);
  const graphics: Graphic[] = [];
  const labels: Graphic[] = [];

  for (const key of structurePolygonsKeys) {
    if (key === 'WaterLevel') continue;
    const points = structurePolygons[key];
    const graphic = new Graphic();
    graphic.geometry = constructPolygonFromInputPoints(points, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
    graphic.symbol = fillSymbol;
    graphics.push(graphic);
    // if (labelFunc) {
    //   const label = labelFunc(graphic.geometry as Polygon, labelParams);
    //   if (label) {
    //     labels.push(label);
    //   }
    // }
  }

  layer.addMany(graphics);
  layer.addMany(labels);
  return layer;
}

export function constructMultiplePolygonLayer(
  title: string,
  inputPoints: InputPoint[][] | undefined,
  fillSymbol: SimpleFillSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  alignedElementId?
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (alignedElementId) layer.id = `intersect_border-${alignedElementId}`;

  if (inputPoints) {
    const graphics: Graphic[] = [];
    for (const p of inputPoints) {
      const graphic = new Graphic();
      graphic.geometry = constructPolygonFromInputPoints(p, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      graphic.symbol = fillSymbol;
      graphics.push(graphic);
    }
    layer.addMany(graphics);
  }
  return layer;
}

export function constructMultiplePolygonLayerL(
  title: string,
  centroidLabel: string,
  inputPoints: InputPoint[][] | undefined,
  fillSymbol: SimpleFillSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  alignedElementId?
): GraphicsLayer[] {
  const layer = new GraphicsLayer();
  layer.title = title;
  if (alignedElementId) layer.id = `intersect_border-${alignedElementId}`;

  const labelLayer = new GraphicsLayer();
  labelLayer.title = centroidLabel;
  const textSymbol = new TextSymbol({
    text: centroidLabel,
    horizontalAlignment: 'center',
    verticalAlignment: 'middle',
    ...__standardLabelSymbol
  });

  if (inputPoints) {
    for (const p of inputPoints) {
      const graphic = new Graphic();
      graphic.geometry = constructPolygonFromInputPoints(p, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      graphic.symbol = fillSymbol;
      layer.graphics.push(graphic);

      const labelPoint = (graphic.geometry as Polygon).centroid;
      const labelGraphic = new Graphic({
        geometry: labelPoint,
        symbol: textSymbol
      });
      labelLayer.graphics.push(labelGraphic);
    }
  }

  return [layer, labelLayer];
}

export function labelPolygonAtCentroid(poly: Polygon, labelParams: any): Graphic {
  const textSymbol = new TextSymbol({
    text: labelParams.text,
    horizontalAlignment: 'center',
    verticalAlignment: 'middle',
    ...__standardLabelSymbol
  });
  const labelPoint = poly.centroid;
  const labelGraphic = new Graphic({
    geometry: labelPoint,
    symbol: textSymbol
  });
  return labelGraphic;
}

// function labelLineAsMultipoint(line: Polyline, labelParams: any): Graphic {
//   const textSymbol = new TextSymbol({
//     text: labelParams.text,
//     ...__standardLabelSymbol
//   });
//   const points: number[][] = line.paths[0];
//   const labelMultipoint = new Multipoint({
//     points: points,
//     hasZ: line.hasZ,
//     spatialReference: line.spatialReference
//   });
//   const labelGraphic = new Graphic({
//     geometry: line, //labelMultipoint,
//     symbol: textSymbol
//   });
//   return labelGraphic;
// }

export function constructWaterwayPolygonLayerL(
  id: string,
  title: string,
  centroidLabel: string,
  inputPoints: InputPoint[][] | undefined,
  fillSymbol: SimpleFillSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  alignedElementId?
): GraphicsLayer[] {
  const [polygonLayer, labelLayer] = constructMultiplePolygonLayerL(
    title,
    centroidLabel,
    inputPoints,
    fillSymbol,
    inputSR,
    outputSR,
    inputElevationUnit,
    outputElevationUnit,
    alignedElementId
  );
  polygonLayer.id = `${id}_polygon`;
  labelLayer.id = `${id}_label`;

  return [polygonLayer, labelLayer];
}

export function constructWaterwayPolygonLabelLayer(
  id: string,
  title: string,
  polygonLayer: GraphicsLayer,
  outputSR: SpatialReference,
  lsbelClass: LabelClass = __polygonBorderLabels
): FeatureLayer {
  const labelGraphics = polygonLayer.graphics.toArray().map((g, i) => {
    let polyline: Geometry;
    if (g.geometry.type === 'polygon') {
      const polygon = g.geometry as Polygon;
      polyline = new Polyline({
        spatialReference: polygon.spatialReference,
        hasZ: true,
        paths: [polygon.rings[0]]
      });
    } else {
      polyline = g.geometry;
    }
    return new Graphic({
      geometry: polyline,
      symbol: __hiddenLineSymbol,
      attributes: { OBJECTID: i + 1, featureType: title },
    });
  });

  const labelLayer = new FeatureLayer({
    id: `${id}_label`,
    title: title,
    spatialReference: outputSR,
    fields: [
      {
        name: 'OBJECTID',
        type: 'oid',
      },
      {
        name: 'featureType',
        type: 'string',
      },
    ],
    objectIdField: 'OBJECTID',
    displayField: 'featureType',
    hasZ: true,
    source: labelGraphics,
    renderer: new SimpleRenderer({ symbol: __hiddenLineSymbol }),
    labelingInfo: [lsbelClass],
  });

  return labelLayer;
}

// function constructComboLayer(
//   id: string,
//   title: string,
//   label: string,
//   polygonLayer: GraphicsLayer,
//   outputSR: SpatialReference
// ): GroupLayer {
//   const labelGraphics = polygonLayer.graphics.toArray().map((g, i) => {
//     const polygon = g.geometry as Polygon;
//     const polyline = new Polyline({
//       spatialReference: polygon.spatialReference,
//       hasZ: true,
//       paths: [polygon.rings[0]]
//     });
//     return new Graphic({
//       geometry: polyline,
//       symbol: __hiddenLineSymbol,
//       attributes: { OBJECTID: i + 1, featureType: label },
//     });
//   });

//   const labelLayer = new FeatureLayer({
//     id: `${id}_label`,
//     title: `${title} Labels`,
//     spatialReference: outputSR,
//     fields: [
//       {
//         name: 'OBJECTID',
//         type: 'oid',
//       },
//       {
//         name: 'featureType',
//         type: 'string',
//       },
//     ],
//     objectIdField: 'OBJECTID',
//     displayField: 'featureType',
//     hasZ: true,
//     source: labelGraphics,
//     renderer: new SimpleRenderer({ symbol: __hiddenLineSymbol}),
//     labelingInfo: [__polygonBorderLabels],
//   });

//   const resultLayer = new GroupLayer({
//     title: title,
//     id: `${id}_combo`,
//     layers: [polygonLayer, labelLayer],
//     listMode: 'hide-children',
//   });
//   return resultLayer;
// }

export function constructMultiplePolygon2DLayer(
  title: string,
  inputPoints: InputPoint[][] | undefined,
  fillSymbol: SimpleFillSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;

  if (inputPoints) {
    const graphics = inputPoints.map((p) => {
      const graphic = new Graphic();
      graphic.geometry = constructPolygon2DFromPoints(p, inputSR, outputSR);
      graphic.symbol = fillSymbol;
      return graphic;
    });
    layer.addMany(graphics);
  }
  return layer;
}

/**
 * Constructs and returns a Polygon graphic from an array of ESRI Points
 */
function constructPolygonGraphicFromEsriPoints(outputSR: SpatialReference, symbol: SimpleFillSymbol, points: Point[]): Graphic {
  const polygon = new Polygon();
  polygon.spatialReference = outputSR;
  polygon.hasZ = true;
  polygon.addRing(points);

  const graphic = new Graphic();
  graphic.geometry = polygon;
  graphic.symbol = symbol;

  return graphic;
}

/**
 * Accepts an array of InputPoints, converts them to ESRI Points, then constructs and returns a 3D Polygon geometry
 */
export function constructPolygonFromInputPoints(
  inputPoints: InputPoint[],
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): Polygon {
  const points = inputPoints.map((p) => {
    const point = constructAndProject3dPoint(p.x!, p.y!, p.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
    return point;
  });

  // create new polygon
  const polygon = new Polygon();
  polygon.spatialReference = outputSR;
  polygon.hasZ = true;
  polygon.addRing(points);
  return polygon;
}

/**
 * Accepts an array of InputPoints, converts them to ESRI Points, then constructs and returns a 2D Polygon geometry
 */
export function constructPolygon2DFromPoints(
  inputPoints: InputPoint[],
  inputSR: SpatialReference,
  outputSR: SpatialReference,
): Polygon {
  const points = inputPoints.map((p) => {
    const point = constructPoint2dGeometry(p.x!, p.y!, inputSR);
    const point2 = esriProjection.project(point, outputSR) as Point;
    return point2;
  });

  // create new polygon
  const polygon = new Polygon();
  polygon.spatialReference = outputSR;
  polygon.hasZ = false;
  polygon.addRing(points);
  return polygon;
}

export function constructLandLevelingPlanePointsLayer(
  landLeveling: LandLevelingResponse,
  inputSR: __esri.SpatialReference,
  outputSR: __esri.SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit,
  title: string
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;
  const { modifiedPlanePoints } = landLeveling;
  if (modifiedPlanePoints) {
    modifiedPlanePoints.forEach((planePoint) => {
      const graphic = new Graphic();
      const point = constructAndProject3dPoint(planePoint.x!, planePoint.y!, planePoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit);
      graphic.geometry = point;

      // Points above the original surface (fill) should be green.
      // Points below the original surface (cut) should be red.
      // Points at the original surface (not cut/fill) should be black.

      if (planePoint.cutOrFill === 0) {
        graphic.symbol = __landLevelingPlanePointsUnmodified;
      } else if (planePoint.cutOrFill! > 0) {
        graphic.symbol = __landLevelingPlanePointsFill;
      } else if (planePoint.cutOrFill! < 0) {
        graphic.symbol = __landLevelingPlanePointsCut;
      }

      layer.add(graphic);
    });
  }

  return layer;
}

function constructLandLevelingBasePointLayer(
  title: string,
  basePoint: InputPoint | undefined,
  pointSymbol: SimpleMarkerSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;

  if (basePoint) {
    const graphic = constructSinglePointGraphic(basePoint.x!, basePoint.y!, basePoint.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit, pointSymbol);
    layer.add(graphic);
  }

  return layer;
}

function constructLandLevelingStakeoutGridPointsLayer(
  title: string,
  stakeoutPoints: InputPoint[] | undefined,
  pointSymbol: SimpleMarkerSymbol,
  inputSR: SpatialReference,
  outputSR: SpatialReference,
  inputElevationUnit: LinearUnit,
  outputElevationUnit: LinearUnit
): GraphicsLayer {
  const layer = new GraphicsLayer();
  layer.title = title;

  if (stakeoutPoints) {
    const graphics = stakeoutPoints.map((value, index) => {
      const graphic = constructSinglePointGraphic(value.x!, value.y!, value.z!, inputSR, outputSR, inputElevationUnit, outputElevationUnit, pointSymbol);

      return graphic;
    });

    layer.addMany(graphics);
  }

  return layer;
}

export async function createSaveThumbnail(mapView: MapView): Promise<any> {
  try {
    const map = mapView.map;
    // Get references to the layers
    const originalExtent = mapView.extent;

    const structurExtent = exteriorExtent as Extent;

    structurExtent.expand(1.2);

    //Zoom to structur extent
    await mapView.goTo(structurExtent);

    // Hide the layers before capturing the screenshot
    const originalContourLayer = map.findLayerById(AppConstants.OriginalContourLayerId);
    const designedContourLayer = map.findLayerById(AppConstants.DesignedContourLayerId);
    const label_points = map.findLayerById("label_points");
    const SurveyPointsLayerId = map.findLayerById(AppConstants.SurveyPointsLayerId);
    const OriginalTinTrianglesLayerId = map.findLayerById(AppConstants.OriginalTinTrianglesLayerId);
    const DesignedTinTrianglesLayerId = map.findLayerById(AppConstants.DesignedTinTrianglesLayerId);

    // Hide the layers before capturing the screenshot
    if (originalContourLayer) originalContourLayer.visible = false;
    if (designedContourLayer) designedContourLayer.visible = false;
    if (label_points) label_points.visible = true;
    if (SurveyPointsLayerId) SurveyPointsLayerId.visible = false;
    if (OriginalTinTrianglesLayerId) OriginalTinTrianglesLayerId.visible = false;
    if (DesignedTinTrianglesLayerId) DesignedTinTrianglesLayerId.visible = false;

    await sleep(1000);

    const dimensions = getDimensionsForImage(structurExtent, mapView);

    // Take a screenshot of the mapView with the specified parameters
    const screenshot = await mapView.takeScreenshot({
      width: dimensions.imageWidth,
      height: dimensions.imageHeight,
      area: dimensions.area,
      format: 'png',
      ignorePadding: true
    });

    // Show the layers again after capturing the screenshot
    if (designedContourLayer) {
      designedContourLayer.visible = true;
    } else {
      if (originalContourLayer) originalContourLayer.visible = true;
    }
    //if (label_points) label_points.destroy();
    const labelLayers = map.allLayers.filter((layer) => {
      return layer.id === "label_points";
    });
    for (const layer of labelLayers) {
      layer.visible = false;
    }

    // Zoom back to originalExtent
    await mapView.goTo(originalExtent);
    return screenshot;
  } catch (error) {
    throw error;
  }
}

export async function turnOffLabelLayer(mapView: MapView) {
  try {
    const map = mapView.map;
    const labelLayers = map.allLayers.filter((layer) => {
      return layer.id === "label_points";
    });
    for (const layer of labelLayers) {
      layer.visible = false;
    }
  } catch (error) {
    throw error;
  }
}